/**
 * API Client pour Manrina - Connexion au backend NestJS
 *
 * Ce fichier remplace l'ancien api.js qui pointait vers api.php (Airtable)
 *
 * INSTRUCTIONS :
 * 1. Copier ce fichier sur O2switch dans le dossier frontend/src/
 * 2. Remplacer l'ancien api.js
 * 3. Tester le login et les autres fonctionnalités
 *
 * @version 2.0 - Migration NestJS
 * @date 2 janvier 2026
 */

// ============================================
// CONFIGURATION
// ============================================

// ANCIENNE API (Airtable via PHP proxy) - gardé pour rollback si besoin
// const API_BASE_URL = '/api.php';

// API NestJS
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://core.manrina.fr';

// Exporte pour les composants qui font des fetch directs (PDF preview, etc.)
export { API_BASE_URL };

// ============================================
// UTILITAIRES IMAGES
// ============================================

/**
 * Corrige les URLs d'images qui peuvent avoir localhost au lieu de l'URL de prod
 * @param {string} url - URL de l'image
 * @returns {string} URL corrigée
 */
export function fixImageUrl(url) {
  if (!url) return url;
  // Remplacer tout localhost (port variable) par l'URL de prod
  if (url.startsWith('http://localhost')) {
    return url.replace(/http:\/\/localhost:\d+/, API_BASE_URL);
  }
  // Chemins relatifs /uploads/...
  if (url.startsWith('/uploads')) {
    return `${API_BASE_URL}${url}`;
  }
  return url;
}

// Cle pour stocker l'utilisateur dans localStorage (info affichage uniquement)
const USER_KEY = 'manrina_user';

// ============================================
// GESTION DE SESSION (cookies httpOnly)
// ============================================

/**
 * Recupere l'utilisateur stocke (cache local, la source de verite est le cookie)
 */
export function getStoredUser() {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
}

/**
 * Stocke l'utilisateur (cache local pour affichage)
 */
export function setStoredUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Nettoie la session locale (le cookie est supprime cote backend)
 */
export function removeToken() {
  localStorage.removeItem(USER_KEY);
  // Nettoyer l'ancien token localStorage si present (migration)
  localStorage.removeItem('manrina_auth_token');
}

/**
 * Verifie si l'utilisateur est connecte (cache local, verifie par /auth/me au demarrage)
 */
export function isAuthenticated() {
  return !!getStoredUser();
}

// Retrocompat — les anciens appels a getToken/setToken ne cassent pas
export function getToken() { return isAuthenticated() ? 'httponly-cookie' : null; }
export function setToken() { /* no-op — le cookie est set par le backend */ }

// ============================================
// CLIENT HTTP GÉNÉRIQUE
// ============================================

/**
 * Effectue une requête HTTP vers l'API NestJS
 * @param {string} endpoint - Le chemin de l'API (ex: '/caisses')
 * @param {object} options - Options fetch (method, body, etc.)
 * @returns {Promise<any>} - Les données de la réponse
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  // Headers par défaut
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    // Gérer les erreurs HTTP
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Si 401, token expiré → déconnexion (sauf pour /auth/me qui est un check de session)
      if (response.status === 401 && !endpoint.includes('/auth/me')) {
        removeToken();
        window.location.href = '/login';
        throw new Error('Session expirée, veuillez vous reconnecter');
      }

      throw new Error(errorData.message || `Erreur ${response.status}`);
    }

    // Réponse vide (204 No Content)
    if (response.status === 204) {
      return null;
    }

    const data = await response.json();

    // L'API NestJS retourne { data: ... }
    // On retourne directement data pour compatibilité avec l'ancien code
    return data.data !== undefined ? data.data : data;
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error.message);
    throw error;
  }
}

// ============================================
// MODULE AUTH
// ============================================

export const auth = {
  /**
   * Connexion utilisateur
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{token: string, user: object}>}
   */
  async login(email, password) {
    const result = await apiRequest('/auth/login', {
      method: 'POST',
      body: { email, password },
    });

    // Stocker l'utilisateur (cache local) — le JWT est dans le cookie httpOnly
    if (result.user) {
      setStoredUser(result.user);
    }

    return result;
  },

  /**
   * Deconnexion — supprime le cookie httpOnly cote backend
   */
  async logout() {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } catch { /* ignore */ }
    removeToken();
    window.location.href = '/login';
  },

  /**
   * Récupérer l'utilisateur connecté
   * @returns {Promise<object>}
   */
  async me() {
    return apiRequest('/auth/me');
  },

  /**
   * Changer le mot de passe
   * @param {string} currentPassword
   * @param {string} newPassword
   */
  async changePassword(currentPassword, newPassword) {
    return apiRequest('/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    });
  },

  /**
   * Mettre à jour son profil
   * @param {object} data - { nom?, prenom?, commune? }
   */
  async updateProfile(data) {
    return apiRequest('/auth/me', {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Vérifier si le setup initial est fait
   */
  async checkSetup() {
    return apiRequest('/auth/check-setup');
  },

  /**
   * Setup initial (création premier admin)
   */
  async setup(data) {
    return apiRequest('/auth/setup', {
      method: 'POST',
      body: data,
    });
  },
};

// ============================================
// MODULE CAISSES
// ============================================

export const caisses = {
  /**
   * Liste toutes les caisses
   */
  async getAll() {
    return apiRequest('/caisses');
  },

  /**
   * Récupère une caisse par ID
   * @param {number} id
   */
  async getById(id) {
    return apiRequest(`/caisses/${id}`);
  },

  /**
   * Crée une nouvelle caisse
   * @param {object} data - { nom, description?, icon? }
   */
  async create(data) {
    return apiRequest('/caisses', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Met à jour une caisse
   * @param {number} id
   * @param {object} data
   */
  async update(id, data) {
    return apiRequest(`/caisses/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Supprime une caisse
   * @param {number} id
   */
  async delete(id) {
    return apiRequest(`/caisses/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// MODULE TRANSACTIONS
// ============================================

export const transactions = {
  /**
   * Liste les transactions avec filtres optionnels
   * @param {object} filters - { caisseId?, userId?, from?, to? }
   */
  async getAll(filters = {}) {
    const params = new URLSearchParams();
    if (filters.caisseId) params.append('caisseId', filters.caisseId);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);

    const query = params.toString();
    return apiRequest(`/transactions${query ? `?${query}` : ''}`);
  },

  /**
   * Récupère une transaction par ID
   * @param {number} id
   */
  async getById(id) {
    return apiRequest(`/transactions/${id}`);
  },

  /**
   * Crée une nouvelle transaction
   * @param {object} data - { motif, montant, type, date, statut, caisseId, categorieId?, note? }
   */
  async create(data) {
    return apiRequest('/transactions', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Met à jour une transaction
   * @param {number} id
   * @param {object} data
   */
  async update(id, data) {
    return apiRequest(`/transactions/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Supprime une transaction
   * @param {number} id
   */
  async delete(id) {
    return apiRequest(`/transactions/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// MODULE CATEGORIES
// ============================================

export const categories = {
  /**
   * Liste les catégories avec filtre optionnel par type
   * @param {string} type - 'ENTREE' ou 'SORTIE' (optionnel)
   */
  async getAll(type) {
    const query = type ? `?type=${type}` : '';
    return apiRequest(`/categories${query}`);
  },

  /**
   * Récupère une catégorie par ID
   * @param {number} id
   */
  async getById(id) {
    return apiRequest(`/categories/${id}`);
  },

  /**
   * Crée une nouvelle catégorie
   * @param {object} data - { label, icon?, type }
   */
  async create(data) {
    return apiRequest('/categories', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Met à jour une catégorie
   * @param {number} id
   * @param {object} data
   */
  async update(id, data) {
    return apiRequest(`/categories/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Supprime une catégorie
   * @param {number} id
   */
  async delete(id) {
    return apiRequest(`/categories/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// MODULE USERS (Équipe)
// ============================================

export const users = {
  /**
   * Liste tous les utilisateurs
   */
  async getAll() {
    return apiRequest('/users');
  },

  /**
   * Liste les membres de l'equipe Manrina (ADMIN + MEMBRE actifs).
   * Format leger destine aux selecteurs (assignation taches, etc.)
   */
  async getEquipe() {
    return apiRequest('/users/equipe');
  },

  /**
   * Récupère un utilisateur par ID
   * @param {number} id
   */
  async getById(id) {
    return apiRequest(`/users/${id}`);
  },

  /**
   * Crée un nouvel utilisateur
   * @param {object} data - { nom, email, password, role }
   */
  async create(data) {
    return apiRequest('/users', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Met à jour un utilisateur
   * @param {number} id
   * @param {object} data
   */
  async update(id, data) {
    return apiRequest(`/users/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Change le rôle d'un utilisateur
   * @param {number} id
   * @param {string} role - 'ADMIN' ou 'MEMBRE'
   */
  async updateRole(id, role) {
    return apiRequest(`/users/${id}/role`, {
      method: 'PATCH',
      body: { role },
    });
  },

  /**
   * Supprime un utilisateur
   * @param {number} id
   */
  async delete(id) {
    return apiRequest(`/users/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Changer le statut d'un utilisateur (suspendre/reactiver)
   */
  async updateStatus(id, status) {
    return apiRequest(`/users/${id}/status`, {
      method: 'PATCH',
      body: { status },
    });
  },

  /**
   * Definir les modules portail accessibles a un utilisateur (feature-gating)
   * @param {string} id - UUID
   * @param {string[]} enabledModules - ex: ['TOURNEES']
   */
  async setModules(id, enabledModules) {
    return apiRequest(`/users/${id}/modules`, {
      method: 'PATCH',
      body: { enabledModules },
    });
  },

  /**
   * Reinitialiser le PIN d'un utilisateur (admin)
   */
  async resetPin(id, newPin) {
    return apiRequest(`/users/${id}/reset-pin`, {
      method: 'POST',
      body: { newPin },
    });
  },

  /**
   * Lister les demandes de reinitialisation PIN
   */
  async getPinResetRequests(status) {
    const query = status ? `?status=${status}` : '';
    return apiRequest(`/users/pin-reset-requests${query}`);
  },

  /**
   * Traiter une demande de reinitialisation PIN
   */
  async treatPinResetRequest(id) {
    return apiRequest(`/users/pin-reset-requests/${id}`, {
      method: 'PATCH',
    });
  },
};

// ============================================
// MODULE EVENEMENTS (ex-MARCHES)
// ============================================

export const marches = {
  /**
   * Liste tous les evenements avec filtre optionnel par statut
   * @param {string} statut - 'PLANIFIE', 'OUVERT', 'TERMINE' (optionnel)
   */
  async getAll(statut) {
    const query = statut ? `?statut=${statut}` : '';
    return apiRequest(`/events${query}`);
  },

  /**
   * Cloture un evenement : emet les factures COM manquantes, puis verrouille
   * (S37). Reversible par un PATCH du statut ("Reactiver").
   * Renvoie l'evenement mis a jour + le compte rendu d'emission `factures`.
   */
  async cloturer(id) {
    return apiRequest(`/events/${id}/cloture`, { method: 'POST' });
  },

  /**
   * Emet les factures COM manquantes SANS cloturer (bouton "factures
   * manquantes"). Les participations sans CA declare sont ignorees.
   */
  async emettreFactures(id) {
    return apiRequest(`/events/${id}/factures`, { method: 'POST' });
  },

  /**
   * Recupere un evenement par ID avec ses stats
   * @param {string} id - UUID
   */
  async getById(id) {
    return apiRequest(`/events/${id}`);
  },

  /**
   * Cree un nouvel evenement
   * @param {object} data - { date, lieu?, commissionDefaut?, notes?, typeConfigId?, parentId? }
   */
  async create(data) {
    return apiRequest('/events', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Met a jour un evenement
   * @param {string} id - UUID
   * @param {object} data
   */
  async update(id, data) {
    return apiRequest(`/events/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Supprime un evenement
   * @param {string} id - UUID
   */
  async delete(id) {
    return apiRequest(`/events/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Recupere les statistiques detaillees d'un evenement
   * @param {string} id - UUID
   */
  async getStats(id) {
    return apiRequest(`/events/${id}/stats`);
  },

  /**
   * Cloture un evenement (TERMINE → CLOTURE)
   * @param {string} id - UUID
   */
  async cloture(id) {
    return apiRequest(`/events/${id}/cloture`, {
      method: 'POST',
    });
  },

  /**
   * Annule un evenement (PLANIFIE/OUVERT → ANNULE)
   * @param {string} id - UUID
   */
  async annuler(id) {
    return apiRequest(`/events/${id}/annuler`, {
      method: 'POST',
    });
  },

  /**
   * Upload l'image (banniere) d'un evenement (admin).
   * L'URL est persistee automatiquement dans Event.bannerImage cote backend.
   * @param {string} id - UUID de l'evenement
   * @param {File} file - Fichier image (JPG/PNG/WebP, max 5 Mo)
   * @returns {Promise<{success: boolean, url: string, filename: string, size: number, event: object}>}
   */
  async uploadBanner(id, file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/events/${id}/banner`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erreur upload' }));
      throw new Error(error.message || 'Erreur lors de l\'upload de l\'image');
    }

    return response.json();
  },
};

// ============================================
// MODULE EXPOSANTS (Organizations)
// ============================================

export const exposants = {
  /**
   * Liste tous les exposants (organizations) avec filtres optionnels
   * @param {object} filters - { status?, search? }
   */
  async getAll(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    const query = params.toString();
    return apiRequest(`/organizations${query ? `?${query}` : ''}`);
  },

  /**
   * Liste tous les exposants avec leurs modules actifs
   * @param {object} filters - { status?, search? }
   */
  async getAllWithModules(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    const query = params.toString();
    return apiRequest(`/organizations/with-modules${query ? `?${query}` : ''}`);
  },

  /**
   * Liste tous les modules disponibles
   */
  async getModules() {
    return apiRequest('/organizations/modules/list');
  },

  /**
   * Met a jour un module (commission par defaut, nom, etc.) — admin uniquement
   */
  async updateModule(id, data) {
    return apiRequest(`/organizations/modules/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Récupère un exposant par ID
   * @param {string} id - UUID
   */
  async getById(id) {
    return apiRequest(`/organizations/${id}`);
  },

  /**
   * Crée un nouvel exposant (organization)
   * @param {object} data - { nom, email?, telephone?, description?, status? }
   */
  async create(data) {
    return apiRequest('/organizations', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Met à jour un exposant
   * @param {string} id - UUID
   * @param {object} data
   */
  async update(id, data) {
    return apiRequest(`/organizations/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Supprime un exposant
   * @param {string} id - UUID
   */
  async delete(id) {
    return apiRequest(`/organizations/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Valide un exposant
   * @param {string} id - UUID
   */
  async validate(id) {
    return apiRequest(`/organizations/${id}/validate`, {
      method: 'POST',
    });
  },

  /**
   * Suspend un exposant
   * @param {string} id - UUID
   * @param {string} reason - Motif de suspension
   */
  async suspend(id, reason) {
    return apiRequest(`/organizations/${id}/suspend`, {
      method: 'POST',
      body: { reason },
    });
  },

  /**
   * Active un module pour un exposant
   * @param {string} id - UUID de l'organisation
   * @param {string} moduleCode - Code du module (MARCHE, TOURNEE...)
   */
  async activateModule(id, moduleCode) {
    return apiRequest(`/organizations/${id}/modules/${moduleCode}/activate`, {
      method: 'POST',
    });
  },

  /**
   * Désactive un module pour un exposant
   * @param {string} id - UUID de l'organisation
   * @param {string} moduleCode - Code du module (MARCHE, TOURNEE...)
   */
  async deactivateModule(id, moduleCode) {
    return apiRequest(`/organizations/${id}/modules/${moduleCode}/deactivate`, {
      method: 'POST',
    });
  },

  /**
   * Attribuer ou changer le proprietaire d'une organisation (admin only).
   * @param {string} id - UUID de l'organisation
   * @param {string|null} ownerId - UUID du nouveau owner, ou null pour detacher
   */
  async setOwner(id, ownerId) {
    return apiRequest(`/organizations/${id}/owner`, {
      method: 'PATCH',
      body: { ownerId },
    });
  },

  /**
   * Pre-visualise la fusion de deux organisations.
   * Lecture seule, retourne le diff que la fusion appliquerait.
   * @param {string} sourceId - UUID de l'org a supprimer
   * @param {string} targetId - UUID de l'org a conserver
   */
  async previewMerge(sourceId, targetId) {
    const qs = new URLSearchParams({ sourceId, targetId }).toString();
    return apiRequest(`/organizations/merge/preview?${qs}`);
  },

  /**
   * Execute la fusion de deux organisations (admin only).
   * @param {string} sourceId - UUID de l'org a supprimer
   * @param {string} targetId - UUID de l'org a conserver
   * @param {boolean} force - true pour forcer en cas de conflit d'ownerId
   * @param {boolean} confirmerPerteHistorique - regle d'or : seul moyen de passer
   *   outre un CA divergent declare des deux cotes sur le meme evenement
   */
  async merge(sourceId, targetId, force = false, confirmerPerteHistorique = false) {
    return apiRequest('/organizations/merge', {
      method: 'POST',
      body: { sourceId, targetId, force, confirmerPerteHistorique },
    });
  },
};

// ============================================
// MODULE PARTICIPATIONS
// ============================================

export const participations = {
  /**
   * Liste les participations avec filtres optionnels
   * @param {object} filters - { marcheId?, organizationId? }
   */
  async getAll(filters = {}) {
    const params = new URLSearchParams();
    if (filters.marcheId) params.append('marcheId', filters.marcheId);
    if (filters.organizationId) params.append('organizationId', filters.organizationId);

    const query = params.toString();
    return apiRequest(`/participations${query ? `?${query}` : ''}`);
  },

  /**
   * Récupère une participation par ID
   * @param {string} id - UUID
   */
  async getById(id) {
    return apiRequest(`/participations/${id}`);
  },

  /**
   * Crée une nouvelle participation
   * @param {object} data - { marcheId, organizationId, statut?, tauxCommission? }
   */
  async create(data) {
    return apiRequest('/participations', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Met à jour une participation
   * @param {number} id
   * @param {object} data
   */
  async update(id, data) {
    return apiRequest(`/participations/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Supprime une participation
   * @param {number} id
   */
  async delete(id) {
    return apiRequest(`/participations/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Marque la presence d'une participation (admin/organisateur).
   * Met a jour statutPresence (nouveau cycle ADR-036) + audit
   * (presenceConfirmedAt/By) et synchronise le statut legacy.
   * @param {string} id - UUID de la participation
   * @param {object} data - { statutPresence: 'PRESENT' | 'ABSENT', notes?: string }
   */
  async updatePresence(id, data) {
    return apiRequest(`/participations/${id}/presence`, {
      method: 'PATCH',
      body: data,
    });
  },
};

// ============================================
// MODULE PRODUCTS (Référentiel produits)
// ============================================

export const products = {
  /**
   * Liste tous les produits avec filtres optionnels
   * @param {object} filters - { status?, categoryId?, search? }
   */
  async getAll(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.categoryId) params.append('categoryId', filters.categoryId);
    if (filters.search) params.append('search', filters.search);

    const query = params.toString();
    return apiRequest(`/products${query ? `?${query}` : ''}`);
  },

  /**
   * Récupère un produit par ID avec ses relations
   * @param {string} id - UUID
   */
  async getById(id) {
    return apiRequest(`/products/${id}`);
  },

  /**
   * Crée un nouveau produit
   * @param {object} data - { nom, categoryId, description?, unit?, photos?, labels?, status? }
   */
  async create(data) {
    return apiRequest('/products', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Met à jour un produit
   * @param {string} id - UUID
   * @param {object} data
   */
  async update(id, data) {
    return apiRequest(`/products/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Supprime un produit (soft delete)
   * @param {string} id - UUID
   */
  async delete(id) {
    return apiRequest(`/products/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Publie un produit (DRAFT → PUBLISHED)
   * @param {string} id - UUID
   */
  async publish(id) {
    return apiRequest(`/products/${id}/publish`, {
      method: 'POST',
    });
  },

  /**
   * Archive un produit
   * @param {string} id - UUID
   */
  async archive(id) {
    return apiRequest(`/products/${id}/archive`, {
      method: 'POST',
    });
  },

  /**
   * Publier plusieurs produits en masse
   * @param {string[]} ids - Liste d'UUIDs
   */
  async bulkPublish(ids) {
    return apiRequest('/products/bulk-publish', {
      method: 'POST',
      body: { ids },
    });
  },

  /**
   * Archiver plusieurs produits en masse
   * @param {string[]} ids - Liste d'UUIDs
   */
  async bulkArchive(ids) {
    return apiRequest('/products/bulk-archive', {
      method: 'POST',
      body: { ids },
    });
  },

  /**
   * Vérifie si un slug existe (anti-doublon)
   * @param {string} slug
   */
  async checkSlug(slug) {
    return apiRequest(`/products/check-slug/${slug}`);
  },

  /**
   * Liste toutes les catégories de produits
   * @param {string} parentId - Filtrer par parent ('root' pour racines)
   */
  async getCategories(parentId) {
    const query = parentId ? `?parentId=${parentId}` : '';
    return apiRequest(`/products/categories${query}`);
  },

  // ============================================
  // CRUD CATÉGORIES DE PRODUITS
  // ============================================

  /**
   * Crée une nouvelle catégorie de produit
   * @param {object} data - { nom, slug?, description?, emoji?, image?, parentId?, ordre?, isActive? }
   */
  async createCategory(data) {
    return apiRequest('/products/categories', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Met à jour une catégorie de produit
   * @param {string} id - UUID
   * @param {object} data
   */
  async updateCategory(id, data) {
    return apiRequest(`/products/categories/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Supprime une catégorie de produit
   * @param {string} id - UUID
   */
  async deleteCategory(id) {
    return apiRequest(`/products/categories/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// API UPLOAD D'IMAGES
// ============================================

export const upload = {
  /**
   * Upload une image
   * @param {File} file - Fichier image
   * @param {string} type - Type de stockage (products, categories, etc.)
   * @returns {Promise<{url: string, filename: string}>}
   */
  async image(file, type = 'products') {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/upload/image?type=${type}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erreur upload' }));
      throw new Error(error.message || 'Erreur lors de l\'upload');
    }

    return response.json();
  },

  /**
   * Upload plusieurs images (max 5)
   * @param {File[]} files - Fichiers images
   * @param {string} type - Type de stockage
   * @returns {Promise<{files: Array<{url: string, filename: string}>}>}
   */
  async images(files, type = 'products') {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    const response = await fetch(`${API_BASE_URL}/upload/images?type=${type}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload error response:', response.status, errorText);
      let errorMessage = 'Erreur lors de l\'upload';
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorMessage;
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Upload API response:', result);
    return result;
  },

  /**
   * Supprime une image
   * @param {string} filename - Nom du fichier
   * @param {string} type - Type de stockage
   */
  async delete(filename, type = 'products') {
    return apiRequest(`/upload/${type}/${filename}`, {
      method: 'DELETE',
    });
  },

  /**
   * Récupère les stats de stockage
   */
  async getStats() {
    return apiRequest('/upload/stats');
  },
};

// ============================================
// CACHE LOCAL
// ============================================

const CACHE_KEY = 'manrina_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Récupère les données du cache
 */
export function getCache() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * Stocke les données dans le cache
 */
export function setCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.warn('Cache write error:', e);
  }
}

/**
 * Vide le cache
 */
export function clearCache() {
  localStorage.removeItem(CACHE_KEY);
}

// ============================================
// NOTATION PRODUCTEURS (chantier 12 MVP2 Tournees)
// ============================================

export const producerNotes = {
  /** Liste les notes d'un producteur + moyennes cachees */
  async getByOrganization(organizationId) {
    return apiRequest(`/organizations/${organizationId}/notes`);
  },
  /** Cree une note (portail conso ; ADMIN ici pour seeder des tests) */
  async create(organizationId, data) {
    return apiRequest(`/organizations/${organizationId}/notes`, {
      method: 'POST',
      body: data,
    });
  },
  /** Modere une note : { statut: 'ACTIVE' | 'REJETEE', reason? } */
  async moderate(organizationId, noteId, data) {
    return apiRequest(`/organizations/${organizationId}/notes/${noteId}/moderate`, {
      method: 'PATCH',
      body: data,
    });
  },
  /** Supprime definitivement une note (purge) */
  async remove(organizationId, noteId) {
    return apiRequest(`/organizations/${organizationId}/notes/${noteId}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// SUIVI KYC STRIPE CONNECT (BO admin, ADR-052)
// ============================================

export const stripeKyc = {
  /** Liste l'etat KYC (cache) de tous les producteurs */
  async list() {
    return apiRequest('/admin/stripe/kyc');
  },
  /** Detail KYC d'un producteur (etat + requirements Stripe en direct) */
  async detail(organizationId) {
    return apiRequest(`/admin/stripe/kyc/${organizationId}`);
  },
  /** Genere un lien d'onboarding a transmettre au producteur (relance) */
  async onboardingLink(organizationId) {
    return apiRequest(`/admin/stripe/kyc/${organizationId}/onboarding-link`, {
      method: 'POST',
    });
  },
};

// ============================================
// MODULE TOURNEES
// ============================================

export const tours = {
  /**
   * Liste toutes les tournées avec filtres optionnels
   * @param {object} filters - { statut? }
   */
  async getAll(filters = {}) {
    const params = new URLSearchParams();
    if (filters.statut) params.append('statut', filters.statut);
    const query = params.toString();
    return apiRequest(`/tours${query ? `?${query}` : ''}`);
  },

  /**
   * Récupère une tournée par ID
   * @param {string} id - UUID
   */
  async getById(id) {
    return apiRequest(`/tours/${id}`);
  },

  /**
   * Crée une nouvelle tournée
   * @param {object} data
   */
  async create(data) {
    return apiRequest('/tours', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Met à jour une tournée
   * @param {string} id - UUID
   * @param {object} data
   */
  async update(id, data) {
    return apiRequest(`/tours/${id}`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Supprime une tournée
   * @param {string} id - UUID
   */
  async delete(id) {
    return apiRequest(`/tours/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Change le statut d'une tournée
   * @param {string} id - UUID
   * @param {string} statut - Nouveau statut
   */
  async updateStatut(id, statut) {
    return apiRequest(`/tours/${id}/statut?statut=${statut}`, {
      method: 'PATCH',
    });
  },

  /**
   * Factures COMMISSION de la tournee (emises a la cloture)
   */
  async getCommissionInvoices(id) {
    return apiRequest(`/tours/${id}/commission-invoices`);
  },

  /**
   * Genere les factures COMMISSION manquantes (idempotent, tournee TERMINE)
   */
  async generateCommissionInvoices(id) {
    return apiRequest(`/tours/${id}/commission-invoices`, { method: 'POST' });
  },

  /**
   * Récupère les offres d'une tournée
   * @param {string} tourId - UUID
   */
  async getOffers(tourId) {
    return apiRequest(`/tours/${tourId}/offers`);
  },

  /**
   * Vue Attribution (Chantier 9C) : produits commandés ayant >= 2 producteurs
   * @param {string} tourId - UUID
   */
  async getAttribution(tourId) {
    return apiRequest(`/tours/${tourId}/attribution`);
  },

  /**
   * Re-balance l'attribution d'un produit entre producteurs (Chantier 9C)
   * @param {string} tourId - UUID
   * @param {object} body - { productId, allocations: [{ tourOfferId, quantite }] }
   */
  async rebalanceAttribution(tourId, body) {
    return apiRequest(`/tours/${tourId}/attribution/rebalance`, {
      method: 'POST',
      body,
    });
  },

  /**
   * Ajoute une offre à une tournée
   * @param {string} tourId - UUID
   * @param {object} data - { productOfferId, quantiteDisponible, prixTournee? }
   */
  async addOffer(tourId, data) {
    return apiRequest(`/tours/${tourId}/offers`, {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Ajoute plusieurs offres a une tournee en une seule requete (bulk).
   * Doublons silencieusement ignores et listes en `skipped`.
   * @param {string} tourId - UUID
   * @param {Array<{productOfferId, quantiteDisponible, quantiteUnit?, prixTournee?}>} items
   * @returns {Promise<{added: TourOffer[], skipped: {productOfferId, raison}[]}>}
   */
  async addOffersBulk(tourId, items) {
    return apiRequest(`/tours/${tourId}/offers/bulk`, {
      method: 'POST',
      body: { items },
    })
  },

  /**
   * Chantier 5b MVP 2 — workflow ad-hoc (mode degrade WhatsApp).
   * Cree Organization (si nouvelle) + Product (si nouveau) + ProductOffer + TourOffer en cascade.
   * @param {string} tourId
   * @param {object} body - { organization: {id?, nom?, telephone?}, product: {id?, nom?, categoryId?, unit?, typeConservation?}, prix, prixUnit?, quantiteDisponible, quantiteUnit? }
   */
  async quickCreateOffer(tourId, body) {
    return apiRequest(`/tours/${tourId}/offers/quick-create`, {
      method: 'POST',
      body,
    })
  },
};

// ============================================
// MODULE POINTS DE RETRAIT
// ============================================

export const pickupPoints = {
  /**
   * Liste tous les points de retrait
   */
  async getAll() {
    return apiRequest('/pickup-points');
  },

  /**
   * Récupère un point de retrait par ID
   * @param {string} id - UUID
   */
  async getById(id) {
    return apiRequest(`/pickup-points/${id}`);
  },

  /**
   * Crée un nouveau point de retrait
   * @param {object} data
   */
  async create(data) {
    return apiRequest('/pickup-points', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Met à jour un point de retrait
   * @param {string} id - UUID
   * @param {object} data
   */
  async update(id, data) {
    return apiRequest(`/pickup-points/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Supprime un point de retrait
   * @param {string} id - UUID
   */
  async delete(id) {
    return apiRequest(`/pickup-points/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// MODULE COMMANDES
// ============================================

export const orders = {
  /**
   * Liste toutes les commandes avec filtres optionnels
   * @param {object} filters - { tourId?, statut?, pickupPointId? }
   */
  async getAll(filters = {}) {
    const params = new URLSearchParams();
    if (filters.tourId) params.append('tourId', filters.tourId);
    if (filters.statut) params.append('statut', filters.statut);
    if (filters.pickupPointId) params.append('pickupPointId', filters.pickupPointId);
    const query = params.toString();
    return apiRequest(`/orders${query ? `?${query}` : ''}`);
  },

  /**
   * Récupère une commande par ID
   * @param {string} id - UUID
   */
  async getById(id) {
    return apiRequest(`/orders/${id}`);
  },

  /**
   * Crée une nouvelle commande
   * @param {object} data
   */
  async create(data) {
    return apiRequest('/orders', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Met à jour une commande
   * @param {string} id - UUID
   * @param {object} data
   */
  async update(id, data) {
    return apiRequest(`/orders/${id}`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Change le statut d'une commande
   * @param {string} id - UUID
   * @param {string} statut - Nouveau statut
   */
  async updateStatut(id, statut) {
    return apiRequest(`/orders/${id}/statut?statut=${statut}`, {
      method: 'PATCH',
    });
  },

  /**
   * Facture VENTE de la commande (emise a la livraison)
   */
  async getInvoice(id) {
    return apiRequest(`/orders/${id}/invoice`);
  },

  /**
   * Marque une commande comme payée
   * @param {string} id - UUID
   * @param {string} mode - Mode de paiement
   */
  async markAsPaid(id, mode) {
    return apiRequest(`/orders/${id}/payer?mode=${mode}`, {
      method: 'PATCH',
    });
  },

  /**
   * Annule une commande
   * @param {string} id - UUID
   */
  async cancel(id) {
    return apiRequest(`/orders/${id}/annuler`, {
      method: 'PATCH',
    });
  },

  /**
   * Supprime une commande
   * @param {string} id - UUID
   */
  async delete(id) {
    return apiRequest(`/orders/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// MODULE OFFRES PRODUITS
// ============================================

export const productOffers = {
  /**
   * Liste toutes les offres avec filtres optionnels
   * @param {object} filters - { organizationId?, moduleId?, moduleCode?, status?, productId? }
   *
   * v0.16.6 : `moduleCode` (ex: 'TOURNEE', 'MARCHE') est un alias de `moduleId`
   * resolu cote serveur. Le filtre par module inclut desormais les canaux
   * additionnels actifs (OR canal principal + channels.some).
   */
  async getAll(filters = {}) {
    const params = new URLSearchParams();
    if (filters.organizationId) params.append('organizationId', filters.organizationId);
    if (filters.moduleId) params.append('moduleId', filters.moduleId);
    if (filters.moduleCode) params.append('moduleCode', filters.moduleCode);
    if (filters.status) params.append('status', filters.status);
    if (filters.productId) params.append('productId', filters.productId);

    const query = params.toString();
    return apiRequest(`/product-offers${query ? `?${query}` : ''}`);
  },

  /**
   * Récupère une offre par ID
   * @param {string} id - UUID
   */
  async getById(id) {
    return apiRequest(`/product-offers/${id}`);
  },

  /**
   * Crée une nouvelle offre
   * @param {object} data - { productId, organizationId, moduleId, territoryId, prix, commissionRate, ... }
   */
  async create(data) {
    return apiRequest('/product-offers', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Met à jour une offre
   * @param {string} id - UUID
   * @param {object} data
   */
  async update(id, data) {
    return apiRequest(`/product-offers/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Supprime une offre
   * @param {string} id - UUID
   */
  async delete(id) {
    return apiRequest(`/product-offers/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Récupère les offres d'une organisation pour un module
   * @param {string} organizationId - UUID
   * @param {string} moduleCode - TOURNEE, MARCHE, etc.
   */
  async getByOrganizationAndModule(organizationId, moduleCode) {
    return apiRequest(`/product-offers/organization/${organizationId}/module/${moduleCode}`);
  },

  // ============================================
  // GESTION DES CANAUX (ProductOfferChannel)
  // ============================================

  /**
   * Récupère les canaux d'une offre
   * @param {string} offerId - UUID
   */
  async getChannels(offerId) {
    return apiRequest(`/product-offers/${offerId}/channels`);
  },

  /**
   * Ajoute un canal à une offre
   * @param {string} offerId - UUID
   * @param {string} moduleId - UUID du module
   * @param {object} options - { prixCanal?, commissionRate? }
   */
  async addChannel(offerId, moduleId, options = {}) {
    return apiRequest(`/product-offers/${offerId}/channels`, {
      method: 'POST',
      body: { moduleId, ...options },
    });
  },

  /**
   * Met à jour un canal
   * @param {string} offerId - UUID
   * @param {string} channelId - UUID
   * @param {object} data - { isActive?, prixCanal?, commissionRate? }
   */
  async updateChannel(offerId, channelId, data) {
    return apiRequest(`/product-offers/${offerId}/channels/${channelId}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Supprime un canal
   * @param {string} offerId - UUID
   * @param {string} channelId - UUID
   */
  async removeChannel(offerId, channelId) {
    return apiRequest(`/product-offers/${offerId}/channels/${channelId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Synchronise les canaux d'une offre (batch add/remove)
   * @param {string} offerId - UUID
   * @param {array} channels - [{ moduleId, prixCanal?, commissionRate? }]
   */
  async syncChannels(offerId, channels) {
    return apiRequest(`/product-offers/${offerId}/channels/sync`, {
      method: 'POST',
      body: { channels },
    });
  },
};

// ============================================
// MODULE TOUR OFFERS (Offres dans une tournée)
// ============================================

export const tourOffers = {
  /**
   * Met à jour une offre de tournée
   * @param {string} tourOfferId - UUID
   * @param {object} data - { quantiteDisponible?, prixTournee?, isActive? }
   */
  async update(tourOfferId, data) {
    return apiRequest(`/tours/offers/${tourOfferId}`, {
      method: 'PATCH',
      body: data,
    });
  },

  /**
   * Retire une offre d'une tournée
   * @param {string} tourOfferId - UUID
   */
  async remove(tourOfferId) {
    return apiRequest(`/tours/offers/${tourOfferId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Chantier 9 MVP 2 — saisie collecte producteur.
   * Met a jour la quantite effectivement recue sur une TourOffer.
   * @param {string} tourOfferId - UUID
   * @param {number|null} quantiteCollectee - Quantite recue (null pour reset)
   */
  async updateCollecte(tourOfferId, quantiteCollectee) {
    return apiRequest(`/tours/offers/${tourOfferId}/collecte`, {
      method: 'PATCH',
      body: { quantiteCollectee },
    });
  },
};

// ============================================
// DEMANDES D'AJUSTEMENT (ADR-059, palier 3 v7.3)
// ============================================
// Corriger ce qui a ete vendu quand la realite ne suit pas. Rien ne s'applique
// sans validation ici : le producteur (ou la saisie de collecte) PROPOSE,
// l'equipe decide. Le remboursement client part sur son moyen de paiement.

export const tourAdjustments = {
  /** File d'attente : demandes filtrables par statut et par tournee. */
  async list({ statut, tourId } = {}) {
    const params = new URLSearchParams()
    if (statut) params.set('statut', statut)
    if (tourId) params.set('tourId', tourId)
    const qs = params.toString()
    return apiRequest(`/admin/tour-adjustments${qs ? `?${qs}` : ''}`)
  },

  /**
   * Creer une demande depuis le BO (ecart de collecte, ou reclamation client
   * recueillie par l'equipe). `repartition` = { orderItemId: quantiteRetiree }
   * pour imposer sa ventilation ; sinon prorata automatique.
   */
  async create({ tourOfferId, origine, quantiteManquante, motif, repartition }) {
    return apiRequest('/admin/tour-adjustments', {
      method: 'POST',
      body: { tourOfferId, origine, quantiteManquante, motif, repartition },
    })
  },

  /** Valider ET appliquer : quantites reduites + remboursements clients. */
  async valider(id) {
    return apiRequest(`/admin/tour-adjustments/${id}/valider`, { method: 'POST' })
  },

  /** Refuser (motif obligatoire) — rien n'est modifie. */
  async refuser(id, motifRefus) {
    return apiRequest(`/admin/tour-adjustments/${id}/refuser`, {
      method: 'POST',
      body: { motifRefus },
    })
  },
};

// ============================================
// MODULE BASKET TEMPLATES (Paniers precomposes par tournee — Chantier 5 MVP 2)
// ============================================

export const basketTemplates = {
  /**
   * Liste les paniers precomposes d'une tournee (avec dispo derivee).
   * @param {string} tourId - UUID
   */
  async listByTour(tourId) {
    return apiRequest(`/tours/${tourId}/baskets`)
  },

  /**
   * Cree un panier precompose pour une tournee.
   * @param {string} tourId - UUID
   * @param {object} data - { nom, description?, prixVente, photo?, ordre?, isActive?, items? }
   */
  async create(tourId, data) {
    return apiRequest(`/tours/${tourId}/baskets`, {
      method: 'POST',
      body: data,
    })
  },

  /**
   * Detail d'un panier (items + dispo derivee).
   * @param {string} basketId - UUID
   */
  async getById(basketId) {
    return apiRequest(`/baskets/${basketId}`)
  },

  /**
   * Modifie les champs d'un panier (nom, prix, description, photo, ordre, isActive).
   * @param {string} basketId - UUID
   * @param {object} data
   */
  async update(basketId, data) {
    return apiRequest(`/baskets/${basketId}`, {
      method: 'PATCH',
      body: data,
    })
  },

  /**
   * Supprime (soft delete) un panier.
   * @param {string} basketId - UUID
   */
  async remove(basketId) {
    return apiRequest(`/baskets/${basketId}`, {
      method: 'DELETE',
    })
  },

  /**
   * Remplace integralement les items du panier (delete-then-create + recompute capacitesRequises).
   * @param {string} basketId - UUID
   * @param {Array<{tourOfferId, quantite, quantiteUnit?, ordre?}>} items
   */
  async replaceItems(basketId, items) {
    return apiRequest(`/baskets/${basketId}/items`, {
      method: 'PUT',
      body: { items },
    })
  },
}

// ============================================
// HELPERS DE FORMATAGE
// ============================================

/**
 * Formate une date ISO en format français
 * @param {string} dateStr - Date ISO
 * @returns {string} - Date formatée (ex: "Sam. 4 Jan")
 */
export function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
  // Utiliser les méthodes UTC pour éviter les décalages de timezone
  return `${days[date.getUTCDay()]}. ${date.getUTCDate()} ${months[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/**
 * Formate une date en format long francais UTC (ex: "samedi 21 fevrier")
 */
export function formatDateLong(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  return `${days[d.getUTCDay()]} ${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
}

/**
 * Formate une date en format court francais UTC (ex: "21/02/2026")
 */
export function formatDateFr(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
}

/**
 * Formate l'heure
 * @param {string} dateStr - Date ISO
 * @returns {string} - Heure formatée (ex: "14:30")
 */
export function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Formate date et heure
 * @param {string} dateStr - Date ISO
 * @returns {string} - Date et heure formatées
 */
export function formatDateTime(dateStr) {
  if (!dateStr) return '';
  return `${formatDate(dateStr)} ${formatTime(dateStr)}`;
}

/**
 * Convertit date et heure en format ISO
 * @param {string} date - Date YYYY-MM-DD
 * @param {string} time - Heure HH:MM
 * @returns {string} - Date ISO
 */
export function toAirtableDateTime(date, time) {
  if (!date) return new Date().toISOString();
  const timeStr = time || '12:00';
  return new Date(`${date}T${timeStr}:00`).toISOString();
}

// ============================================
// MODULE BACKUP (Admin)
// ============================================

export const backup = {
  /**
   * Lister tous les backups
   */
  async list() {
    return apiRequest('/backup/list');
  },

  /**
   * Obtenir les statistiques de backup
   */
  async stats() {
    return apiRequest('/backup/stats');
  },

  /**
   * Créer un nouveau backup
   * @param {string} [label] - Label optionnel
   */
  async create(label) {
    return apiRequest('/backup/create', {
      method: 'POST',
      body: label ? { label } : {},
    });
  },

  /**
   * Restaurer un backup
   * @param {string} filename - Nom du fichier backup
   */
  async restore(filename) {
    return apiRequest(`/backup/restore/${encodeURIComponent(filename)}`, {
      method: 'POST',
    });
  },

  /**
   * Telecharger un backup
   * @param {string} filename - Nom du fichier backup
   */
  async download(filename) {
    const response = await fetch(`${API_BASE_URL}/backup/download/${encodeURIComponent(filename)}`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Erreur telechargement');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  /**
   * Supprimer un backup
   * @param {string} filename - Nom du fichier backup
   */
  async delete(filename) {
    return apiRequest(`/backup/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Valider l'intégrité des données
   */
  async validate() {
    return apiRequest('/backup/validate');
  },
};

// ============================================
// MODULE TACHES (Retro-planning evenements)
// ============================================

export const events = {
  async getAll(filters = {}) {
    const params = new URLSearchParams();
    // Accepte eventId ou marcheId (retrocompat)
    if (filters.eventId) params.append('eventId', filters.eventId);
    else if (filters.marcheId) params.append('marcheId', filters.marcheId);
    if (filters.type) params.append('type', filters.type);
    if (filters.statut) params.append('statut', filters.statut);
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest(`/event-taches${query}`);
  },

  async getById(id) {
    return apiRequest(`/event-taches/${id}`);
  },

  async create(data) {
    return apiRequest('/event-taches', {
      method: 'POST',
      body: data,
    });
  },

  async update(id, data) {
    return apiRequest(`/event-taches/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  async delete(id) {
    return apiRequest(`/event-taches/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Regularisation des anciens marches (ADMIN, S72) — simulation par defaut.
   * @param {{ appliquer?: boolean, cloreSansCa?: boolean, avant?: string }} body
   * @returns {Promise<{ mode, avant, totaux, lignes }>}
   */
  async regulariserAnciensMarches(body = {}) {
    return apiRequest('/admin/regularisation-marches', { method: 'POST', body });
  },
};

// Alias semantique : "taches" = ce que represente vraiment ce module (rétro-planning).
// Le nom "events" est un legacy du refactor v9.30 (avant : EventTache s'appelait Event).
// Utiliser "taches" dans le nouveau code, "events" reste pour rétrocompat.
export const taches = events;

// ============================================
// TYPES D'EVENEMENTS (configuration)
// ============================================

export const eventTypeConfigs = {
  async getAll() {
    return apiRequest('/event-type-configs');
  },

  async getById(id) {
    return apiRequest(`/event-type-configs/${id}`);
  },

  async create(data) {
    return apiRequest('/event-type-configs', {
      method: 'POST',
      body: data,
    });
  },

  async update(id, data) {
    return apiRequest(`/event-type-configs/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  async delete(id) {
    return apiRequest(`/event-type-configs/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// INTERVENANTS EVENEMENTS
// ============================================

export const eventIntervenants = {
  async getAll(eventId) {
    const query = eventId ? `?eventId=${eventId}` : '';
    return apiRequest(`/event-intervenants${query}`);
  },

  async getById(id) {
    return apiRequest(`/event-intervenants/${id}`);
  },

  async create(data) {
    return apiRequest('/event-intervenants', {
      method: 'POST',
      body: data,
    });
  },

  async update(id, data) {
    return apiRequest(`/event-intervenants/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  async delete(id) {
    return apiRequest(`/event-intervenants/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// INSCRIPTIONS EVENEMENTS
// ============================================

export const eventInscriptions = {
  async getAll(eventId) {
    const query = eventId ? `?eventId=${eventId}` : '';
    return apiRequest(`/event-inscriptions${query}`);
  },

  async getById(id) {
    return apiRequest(`/event-inscriptions/${id}`);
  },

  async create(data) {
    return apiRequest('/event-inscriptions', {
      method: 'POST',
      body: data,
    });
  },

  async update(id, data) {
    return apiRequest(`/event-inscriptions/${id}`, {
      method: 'PATCH',
      body: data,
    });
  },

  async delete(id) {
    return apiRequest(`/event-inscriptions/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// PARAMETRES PORTAIL
// ============================================

export const portalSettings = {
  /**
   * Récupérer les paramètres du portail
   */
  async get() {
    return apiRequest('/public/portal-settings');
  },

  /**
   * Mettre à jour les paramètres du portail (admin only)
   */
  async update(settings) {
    return apiRequest('/portal-settings', {
      method: 'PATCH',
      body: settings,
    });
  },
};

// ============================================
// MODULE DEMANDES DE RATTACHEMENT (Admin)
// ============================================

export const claimRequests = {
  /**
   * Liste les demandes de rattachement avec filtre optionnel par statut
   * @param {string} status - 'PENDING', 'APPROVED', 'REJECTED' (optionnel)
   */
  async getAll(status) {
    const query = status ? `?status=${status}` : '';
    return apiRequest(`/producer/claims${query}`);
  },

  /**
   * Approuver une demande
   * @param {string} id - UUID
   */
  async approve(id) {
    return apiRequest(`/producer/claims/${id}/approve`, {
      method: 'PATCH',
    });
  },

  /**
   * Refuser une demande
   * @param {string} id - UUID
   * @param {string} reason - Motif de refus (optionnel)
   */
  async reject(id, reason) {
    return apiRequest(`/producer/claims/${id}/reject`, {
      method: 'PATCH',
      body: { reason },
    });
  },
};

// ============================================
// MODULE PROPOSITIONS PRODUITS (Admin)
// ============================================

export const productProposals = {
  async getAll() {
    return apiRequest('/producer/product-proposals');
  },

  async approve(id, { approveExclusive = true } = {}) {
    return apiRequest(`/producer/product-proposals/${id}/approve`, {
      method: 'PATCH',
      body: { approveExclusive },
    });
  },

  async reject(id) {
    return apiRequest(`/producer/product-proposals/${id}/reject`, {
      method: 'PATCH',
    });
  },
};

// ============================================
// MODULE FACTURES (Invoices)
// ============================================

export const invoices = {
  /**
   * Liste les factures avec filtres (sans pagination, vue legacy)
   * @param {object} filters - { organizationId?, eventId?, statut?, from?, to? }
   */
  async getAll(filters = {}) {
    const params = new URLSearchParams();
    if (filters.organizationId) params.append('organizationId', filters.organizationId);
    if (filters.eventId) params.append('eventId', filters.eventId);
    if (filters.statut) params.append('statut', filters.statut);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    const query = params.toString();
    return apiRequest(`/invoices${query ? `?${query}` : ''}`);
  },

  /**
   * Liste paginee des factures avec total + hasMore.
   * Utilisee par la page detail organisation (BO).
   * @param {object} filters - { organizationId?, statut?, from?, to?, limit?, offset? }
   * @returns {Promise<{ data: Invoice[], total: number, limit: number, offset: number, hasMore: boolean }>}
   */
  async getAllPaginated(filters = {}) {
    const params = new URLSearchParams();
    if (filters.organizationId) params.append('organizationId', filters.organizationId);
    if (filters.statut) params.append('statut', filters.statut);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.limit != null) params.append('limit', String(filters.limit));
    if (filters.offset != null) params.append('offset', String(filters.offset));
    const query = params.toString();
    return apiRequest(`/invoices/paginated${query ? `?${query}` : ''}`);
  },

  /**
   * Diagnostic : counts bruts factures / orgs / participations
   */
  async getDiagnostic() {
    return apiRequest('/invoices/_diag');
  },

  /**
   * Agregation par organisation (vue cards-par-org du BO).
   * @param {object} filters - { search?, onlyImpaye?, limit?, offset? }
   * @returns {Promise<{ data: OrgAggregation[], total, limit, offset, hasMore }>}
   */
  async getByOrganization(filters = {}) {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.onlyImpaye) params.append('onlyImpaye', 'true');
    if (filters.limit != null) params.append('limit', String(filters.limit));
    if (filters.offset != null) params.append('offset', String(filters.offset));
    const query = params.toString();
    return apiRequest(`/invoices/by-organization${query ? `?${query}` : ''}`);
  },

  /**
   * Recupere une facture par ID
   * @param {string} id - UUID
   */
  async getById(id) {
    return apiRequest(`/invoices/${id}`);
  },

  /**
   * Genere une facture depuis une participation
   * @param {string} participationId - UUID
   * @param {string} notes - Notes optionnelles
   */
  async create(participationId, notes) {
    return apiRequest('/invoices', {
      method: 'POST',
      body: { participationId, notes },
    });
  },

  /**
   * Generation en lot
   * @param {string} marcheId - UUID (optionnel)
   */
  async purge() {
    return apiRequest('/invoices/purge', {
      method: 'POST',
    });
  },

  async generateBatch(marcheId) {
    const query = marcheId ? `?marcheId=${marcheId}` : '';
    return apiRequest(`/invoices/batch${query}`, {
      method: 'POST',
    });
  },

  /**
   * Marquer comme payee
   * @param {string} id - UUID
   * @param {string} modePaiement - Mode de paiement
   */
  async markAsPaid(id, modePaiement) {
    return apiRequest(`/invoices/${id}/pay`, {
      method: 'PATCH',
      body: { modePaiement },
    });
  },

  /**
   * Annuler le paiement d'une facture (remettre en EMISE)
   * @param {string} id - UUID
   */
  async markAsUnpaid(id) {
    return apiRequest(`/invoices/${id}/unpay`, {
      method: 'PATCH',
    });
  },

  /**
   * Annuler une facture
   * @param {string} id - UUID
   */
  async cancel(id) {
    return apiRequest(`/invoices/${id}/cancel`, {
      method: 'PATCH',
    });
  },

  /**
   * Telecharger le PDF
   * @param {string} id - UUID
   */
  async downloadPdf(id) {
    const response = await fetch(`${API_BASE_URL}/invoices/${id}/pdf`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Erreur ${response.status}`);
    }
    return response.blob();
  },

  /**
   * Telecharger un ZIP de plusieurs factures PDF
   * @param {string[]} ids - Liste des UUIDs
   */
  async bulkDownloadPdf(ids) {
    const response = await fetch(`${API_BASE_URL}/invoices/bulk-pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ids }),
    });
    if (!response.ok) {
      throw new Error(`Erreur ${response.status}`);
    }
    return response.blob();
  },

  /**
   * Recuperer la config facturation
   */
  async getBillingConfig() {
    return apiRequest('/invoices/billing-config');
  },

  /**
   * Mettre a jour la config facturation
   * @param {object} data - Cles billing.*
   */
  async updateBillingConfig(data) {
    return apiRequest('/invoices/billing-config', {
      method: 'PATCH',
      body: data,
    });
  },
};

// ============================================
// MODULE NOTIFICATIONS (Admin)
// ============================================

export const notifications = {
  async getAll(type) {
    const query = type ? `?type=${type}` : '';
    return apiRequest(`/notifications${query}`);
  },

  async send(data) {
    return apiRequest('/notifications/send', {
      method: 'POST',
      body: data,
    });
  },

  async getSubscribers() {
    return apiRequest('/notifications/subscribers');
  },

  async deleteSubscriber(id) {
    return apiRequest(`/notifications/subscribers/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// TRANSFORMATION (clients B2B, commandes, livraisons)
// ============================================

export const transformation = {
  // Clients
  getClients: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
    if (filters.search) params.append('search', filters.search);
    const qs = params.toString();
    return apiRequest(`/transformation/clients${qs ? `?${qs}` : ''}`);
  },
  getClient: (id) => apiRequest(`/transformation/clients/${id}`),
  createClient: (data) => apiRequest('/transformation/clients', { method: 'POST', body: data }),
  updateClient: (id, data) => apiRequest(`/transformation/clients/${id}`, { method: 'PATCH', body: data }),
  suspendClient: (id, motif) => apiRequest(`/transformation/clients/${id}/suspend`, { method: 'POST', body: { motif } }),
  reactivateClient: (id) => apiRequest(`/transformation/clients/${id}/reactivate`, { method: 'POST' }),
  deleteClient: (id) => apiRequest(`/transformation/clients/${id}`, { method: 'DELETE' }),
  importClients: (rows) => apiRequest('/transformation/clients/import', { method: 'POST', body: { rows } }),

  // Tournees de livraison
  getTours: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.statutWorkflow) params.append('statutWorkflow', filters.statutWorkflow);
    const qs = params.toString();
    return apiRequest(`/transformation/tours${qs ? `?${qs}` : ''}`);
  },
  getTour: (id) => apiRequest(`/transformation/tours/${id}`),
  createTour: (data) => apiRequest('/transformation/tours', { method: 'POST', body: data }),
  updateTour: (id, data) => apiRequest(`/transformation/tours/${id}`, { method: 'PATCH', body: data }),
  deleteTour: (id) => apiRequest(`/transformation/tours/${id}`, { method: 'DELETE' }),
  addClientToTour: (tourId, clientId, ordre) => apiRequest(`/transformation/tours/${tourId}/clients`, { method: 'POST', body: { clientId, ordre } }),
  removeClientFromTour: (tourId, clientId) => apiRequest(`/transformation/tours/${tourId}/clients/${clientId}`, { method: 'DELETE' }),
  setTourClientLines: (tourId, clientId, lines) => apiRequest(`/transformation/tours/${tourId}/clients/${clientId}/lines`, { method: 'PUT', body: { lines } }),

  // Workflow tournee
  generateTourInstance: (templateId, dateTournee) => apiRequest(`/transformation/tours/${templateId}/generate-instance`, { method: 'POST', body: { dateTournee } }),
  advanceTour: (id) => apiRequest(`/transformation/tours/${id}/advance`, { method: 'POST' }),
  goToTourStep: (id, statut) => apiRequest(`/transformation/tours/${id}/go-to-step`, { method: 'POST', body: { statut } }),
  closeTourAndCreateNext: (id) => apiRequest(`/transformation/tours/${id}/close-and-create-next`, { method: 'POST' }),
  getProductionTotals: (tourId) => apiRequest(`/transformation/tours/${tourId}/production-totals`),

  // Livraisons
  getDeliveries: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.date) params.append('date', filters.date);
    if (filters.clientId) params.append('clientId', filters.clientId);
    if (filters.statut) params.append('statut', filters.statut);
    if (filters.tourId) params.append('tourId', filters.tourId);
    const qs = params.toString();
    return apiRequest(`/transformation/deliveries${qs ? `?${qs}` : ''}`);
  },
  getDelivery: (id) => apiRequest(`/transformation/deliveries/${id}`),
  createDelivery: (data) => apiRequest('/transformation/deliveries', { method: 'POST', body: data }),
  updateDeliveryStatut: (id, statut) => apiRequest(`/transformation/deliveries/${id}`, { method: 'PATCH', body: { statut } }),
  signDelivery: (id, data) => apiRequest(`/transformation/deliveries/${id}/sign`, { method: 'POST', body: data }),

  // Offres Manrina (produits de l'org systeme)
  getManrinaOffers: () => apiRequest('/transformation/offers'),
  createManrinaOffer: (data) => apiRequest('/transformation/offers', { method: 'POST', body: data }),
  updateManrinaOffer: (id, data) => apiRequest(`/transformation/offers/${id}`, { method: 'PATCH', body: data }),
  deleteManrinaOffer: (id) => apiRequest(`/transformation/offers/${id}`, { method: 'DELETE' }),

  // Dashboard
  getDashboard: () => apiRequest('/transformation/dashboard'),

  // Recettes
  getRecipes: () => apiRequest('/transformation/recipes'),
  getRecipe: (id) => apiRequest(`/transformation/recipes/${id}`),
  getRecipeByOffer: (offerId) => apiRequest(`/transformation/recipes/by-offer/${offerId}`),
  getRecipeCost: (id) => apiRequest(`/transformation/recipes/${id}/cout`),
  createRecipe: (data) => apiRequest('/transformation/recipes', { method: 'POST', body: data }),
  updateRecipe: (id, data) => apiRequest(`/transformation/recipes/${id}`, { method: 'PATCH', body: data }),
  deleteRecipe: (id) => apiRequest(`/transformation/recipes/${id}`, { method: 'DELETE' }),
  addRecipeIngredient: (recipeId, data) => apiRequest(`/transformation/recipes/${recipeId}/ingredients`, { method: 'POST', body: data }),
  updateRecipeIngredient: (id, data) => apiRequest(`/transformation/recipes/ingredients/${id}`, { method: 'PATCH', body: data }),
  removeRecipeIngredient: (id) => apiRequest(`/transformation/recipes/ingredients/${id}`, { method: 'DELETE' }),

  // Validation production (verifier stock avant passage en PRODUCTION)
  validateProduction: (tourId) => apiRequest(`/transformation/tours/${tourId}/validate-production`),

  // Stock
  getStockBalances: () => apiRequest('/transformation/stock/balances'),
  getStockAlerts: () => apiRequest('/transformation/stock/alerts'),
  getStockBalance: (offerId) => apiRequest(`/transformation/stock/balance/${offerId}`),
  getStockMovements: (filters) => {
    const params = new URLSearchParams()
    if (filters?.productOfferId) params.set('productOfferId', filters.productOfferId)
    if (filters?.type) params.set('type', filters.type)
    if (filters?.from) params.set('from', filters.from)
    if (filters?.to) params.set('to', filters.to)
    if (filters?.fournisseurId) params.set('fournisseurId', filters.fournisseurId)
    if (filters?.limit) params.set('limit', String(filters.limit))
    return apiRequest(`/transformation/stock/movements?${params}`)
  },
  createStockMovement: (data) => apiRequest('/transformation/stock/movements', { method: 'POST', body: data }),
  simulateProduction: (recipeId, quantite) => apiRequest('/transformation/stock/simulate', { method: 'POST', body: { recipeId, quantite } }),

  // Pertes de production (Phase 3)
  getProductionLosses: (filters = {}) => {
    const params = new URLSearchParams()
    if (filters.productOfferId) params.set('productOfferId', filters.productOfferId)
    if (filters.tourId) params.set('tourId', filters.tourId)
    if (filters.fournisseurId) params.set('fournisseurId', filters.fournisseurId)
    if (filters.dateDebut) params.set('dateDebut', filters.dateDebut)
    if (filters.dateFin) params.set('dateFin', filters.dateFin)
    return apiRequest(`/transformation/losses?${params}`)
  },
  createProductionLoss: (data) => apiRequest('/transformation/losses', { method: 'POST', body: data }),
  deleteProductionLoss: (id) => apiRequest(`/transformation/losses/${id}`, { method: 'DELETE' }),

  // Retours clients (Phase 3)
  getClientReturns: (filters = {}) => {
    const params = new URLSearchParams()
    if (filters.clientId) params.set('clientId', filters.clientId)
    if (filters.productOfferId) params.set('productOfferId', filters.productOfferId)
    if (filters.deliveryId) params.set('deliveryId', filters.deliveryId)
    if (filters.dateDebut) params.set('dateDebut', filters.dateDebut)
    if (filters.dateFin) params.set('dateFin', filters.dateFin)
    return apiRequest(`/transformation/returns?${params}`)
  },
  createClientReturn: (data) => apiRequest('/transformation/returns', { method: 'POST', body: data }),
  deleteClientReturn: (id) => apiRequest(`/transformation/returns/${id}`, { method: 'DELETE' }),

  // Charges organisation (Phase 3 - ADR-033)
  getCharges: (filters = {}) => {
    const params = new URLSearchParams()
    if (filters.type) params.set('type', filters.type)
    return apiRequest(`/transformation/charges?${params}`)
  },
  getCharge: (id) => apiRequest(`/transformation/charges/${id}`),
  createCharge: (data) => apiRequest('/transformation/charges', { method: 'POST', body: data }),
  updateCharge: (id, data) => apiRequest(`/transformation/charges/${id}`, { method: 'PATCH', body: data }),
  deleteCharge: (id) => apiRequest(`/transformation/charges/${id}`, { method: 'DELETE' }),
  getChargeMovements: (id) => apiRequest(`/transformation/charges/${id}/movements`),
  createChargeMovement: (id, data) => apiRequest(`/transformation/charges/${id}/movements`, { method: 'POST', body: data }),

  // Analytics / P&L (Phase 3 Session 2)
  getProductMargins: (filters = {}) => {
    const params = new URLSearchParams()
    if (filters.from) params.set('from', filters.from)
    if (filters.to) params.set('to', filters.to)
    return apiRequest(`/transformation/analytics/margins?${params}`)
  },
  getProductMarginHistory: (productOfferId, filters = {}) => {
    const params = new URLSearchParams()
    if (filters.nbPeriods) params.set('nbPeriods', String(filters.nbPeriods))
    if (filters.periodType) params.set('periodType', filters.periodType)
    return apiRequest(`/transformation/analytics/margins/${productOfferId}/history?${params}`)
  },
  getPnL: (from, to) => {
    const params = new URLSearchParams()
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    return apiRequest(`/transformation/analytics/pnl?${params}`)
  },
  getBreakeven: (productOfferId, fixedCosts) => {
    const params = new URLSearchParams()
    if (fixedCosts) params.set('fixedCosts', String(fixedCosts))
    return apiRequest(`/transformation/analytics/breakeven/${productOfferId}?${params}`)
  },
  getMarginAlerts: (options = {}) => {
    const params = new URLSearchParams()
    if (options.seuilPercent !== undefined) params.set('seuilPercent', String(options.seuilPercent))
    if (options.seuilChutePercent !== undefined) params.set('seuilChutePercent', String(options.seuilChutePercent))
    return apiRequest(`/transformation/analytics/alerts?${params}`)
  },

  // Historique des prix (Phase 3 Session 3 - ADR-034)
  getOfferPriceHistory: (offerId) => apiRequest(`/transformation/offers/${offerId}/price-history`),
  getChargePriceHistory: (chargeId) => apiRequest(`/transformation/charges/${chargeId}/price-history`),

  // Factures generiques (Phase 3 Session 4 - ADR-035 + ADR-036 avoirs/import Zefyr)
  getModuleInvoices: (filters = {}) => {
    const params = new URLSearchParams()
    if (filters.moduleCode) params.set('moduleCode', filters.moduleCode)
    if (filters.clientId) params.set('clientId', filters.clientId)
    if (filters.organizationId) params.set('organizationId', filters.organizationId)
    if (filters.tourId) params.set('tourId', filters.tourId)
    if (filters.orderId) params.set('orderId', filters.orderId)
    if (filters.statut) params.set('statut', filters.statut)
    if (filters.type) params.set('type', filters.type)
    if (filters.from) params.set('from', filters.from)
    if (filters.to) params.set('to', filters.to)
    if (filters.limit) params.set('limit', String(filters.limit))
    return apiRequest(`/module-invoices?${params}`)
  },
  getModuleInvoice: (id) => apiRequest(`/module-invoices/${id}`),
  createInvoiceFromDelivery: (deliveryId) => apiRequest(`/module-invoices/from-delivery/${deliveryId}`, { method: 'POST' }),
  // ADR-036 : creation manuelle facture/avoir (lie ou libre)
  createManualInvoice: (data) => apiRequest(`/module-invoices/manual`, { method: 'POST', body: data }),
  // ADR-036 : import en masse depuis Zefyr (CSV pre-genere par script Python)
  importZefyrInvoices: (rows) => apiRequest(`/module-invoices/import-zefyr`, { method: 'POST', body: { rows } }),
  updateModuleInvoice: (id, data) => apiRequest(`/module-invoices/${id}`, { method: 'PATCH', body: data }),
  validateModuleInvoice: (id) => apiRequest(`/module-invoices/${id}/validate`, { method: 'POST' }),
  payModuleInvoice: (id, modePaiement) => apiRequest(`/module-invoices/${id}/pay`, { method: 'POST', body: { modePaiement } }),
  unpayModuleInvoice: (id) => apiRequest(`/module-invoices/${id}/unpay`, { method: 'POST' }),
  // Tournees : versement d'une facture COMMISSION (compensation effectuee)
  markPaidOutModuleInvoice: (id, payoutReference) =>
    apiRequest(`/module-invoices/${id}/mark-paid-out`, { method: 'PATCH', body: { payoutReference } }),
  cancelModuleInvoice: (id) => apiRequest(`/module-invoices/${id}/cancel`, { method: 'POST' }),
  deleteModuleInvoice: (id) => apiRequest(`/module-invoices/${id}`, { method: 'DELETE' }),
  downloadModuleInvoicePdf: async (id) => {
    const res = await fetch(`${API_BASE_URL}/module-invoices/${id}/pdf`, { credentials: 'include' })
    if (!res.ok) throw new Error('Erreur telechargement PDF')
    return res.blob()
  },

  // PDF
  downloadBonDeCommande: async (tourId) => {
    const res = await fetch(`${API_BASE_URL}/transformation/tours/${tourId}/bon-de-commande`, { credentials: 'include' });
    if (!res.ok) throw new Error('Erreur telechargement PDF');
    return res.blob();
  },
  downloadFicheProduction: async (tourId) => {
    const res = await fetch(`${API_BASE_URL}/transformation/tours/${tourId}/fiche-production`, { credentials: 'include' });
    if (!res.ok) throw new Error('Erreur telechargement PDF');
    return res.blob();
  },
  downloadBonsDeLivraison: async (tourId) => {
    const res = await fetch(`${API_BASE_URL}/transformation/tours/${tourId}/bons-de-livraison`, { credentials: 'include' });
    if (!res.ok) throw new Error('Erreur telechargement PDF');
    return res.blob();
  },
};

// ============================================
// EMOJIS DISPONIBLES
// ============================================

export const EMOJIS = [
  '💰', '💵', '💳', '🏦', '📦', '🛒', '🍎', '🥕', '🥬', '🍞',
  '🧀', '🥛', '🍖', '🐟', '🍳', '☕', '🧃', '🍺', '🍷', '💊',
  '🧴', '🧹', '🔧', '⛽', '🚗', '🏠', '💡', '📱', '💻', '📄',
  '✂️', '🎁', '🎉', '🏪', '📈', '📉', '💼', '👕', '👗', '👟',
  '🌺', '🌿', '🎨', '📚', '🎵', '⚽', '🏃', '💪', '❤️', '⭐'
];

// ============================================
// EXPORT PAR DÉFAUT (pour compatibilité)
// ============================================

const api = {
  auth,
  caisses,
  transactions,
  categories,
  users,
  marches,
  exposants,
  participations,
  products,
  upload,
  tours,
  pickupPoints,
  orders,
  backup,
  events,
  portalSettings,
  claimRequests,
  productProposals,
  invoices,
  notifications,
  transformation,
  // Helpers auth
  getToken,
  setToken,
  removeToken,
  isAuthenticated,
  getStoredUser,
  // Helpers cache
  getCache,
  setCache,
  clearCache,
  // Helpers format
  formatDate,
  formatDateLong,
  formatDateFr,
  formatTime,
  formatDateTime,
  toAirtableDateTime,
  EMOJIS,
};

export default api;
